/**
 * UnderwritingWorkspace.jsx — Underwriting Hub deal workspace.
 *
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │  Header (deal name · SF# · status · layout toggle · Monday link)│
 *   ├──────────────────┬──────────────────────────────────────────────┤
 *   │ FunnelSidebar    │              ChatPanel                       │
 *   │ (vertical funnel)│  (full conversation + input)                 │
 *   │                  │                                              │
 *   └──────────────────┴──────────────────────────────────────────────┘
 *
 * Layout toggle (header):
 *   - underwrite  : funnel (40%) + chat (60%)              ← DEFAULT
 *   - split       : deal context (33%) + funnel (33%) + chat (33%)
 *   - deal        : deal context full-width (no funnel/chat)
 *
 * Behaviour:
 *   - Loads existing session on open → rebuilds funnel state from tool_history
 *   - "Start analysis" button (in FunnelSidebar) kicks off auto-run sequence
 *   - Bot runs through stages sequentially, only pausing for explicit user input
 *     (company confirm, director pick, BS file pick, offer numbers)
 *   - Any tool error / hard-fail stops the run; user can chat to override
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2,
  ExternalLink,
  Building2,
  MessageSquare,
  AlertCircle,
  Paperclip,
  Download,
  Layout,
  Maximize2,
  Bot,
  RotateCcw,
  FileText,
  Copy,
  Send,
} from "lucide-react";

import FunnelSidebar from "./FunnelSidebar";
import ChatPanel     from "./ChatPanel";
import { errorDetailMessage } from "@/lib/errorDetailMessage";


// ── Stage flow contract — keep in sync with sf-uw-service ────────────────
// The bot runs these sequentially during a "Start analysis" sweep.
// A stage that needs user input declares `pauseAfter` or `pauseBefore`.
//
// Arg-shape contract (probed against sf-uw-service Feb 2026):
//   company_validate : { company_number }
//   eligibility      : { company_number, director, is_homeowner }
//   credit_company   : { company_name, company_number }
//   director_credit  : { director_first_name, director_last_name, is_homeowner }
//   director_network : { company_name, company_number, director }
//   reputation       : { company_name }
const AUTO_FLOW = [
  // Eligibility cluster
  { stage: "company_search",   needs: [],                       pauseAfter: "awaiting_confirm",   buildArgs: (ctx) => ({ q: stripSfPrefix(ctx.deal?.name || "") }) },
  { stage: "company_validate", needs: ["confirmedCrn"],         buildArgs: (ctx) => ({ company_number: ctx.confirmedCrn }) },
  { stage: "eligibility",      needs: ["confirmedCrn"],         pauseBefore: "awaiting_director", buildArgs: (ctx) => ({
      company_number: ctx.confirmedCrn,
      director:       ctx.chosenDirector || undefined,
      is_homeowner:   !!ctx.isHomeowner,
  })},
  // Credit cluster
  { stage: "credit_company",   needs: ["confirmedCrn", "confirmedName"], buildArgs: (ctx) => ({
      company_name:   ctx.confirmedName,
      company_number: ctx.confirmedCrn,
  })},
  { stage: "director_credit",  needs: ["chosenDirector"],       buildArgs: (ctx) => {
      const { first, last } = splitDirectorName(ctx.chosenDirector);
      return {
        director_first_name: first,
        director_last_name:  last,
        is_homeowner:        !!ctx.isHomeowner,
      };
  }},
  // Network & reputation
  { stage: "director_network", needs: ["confirmedCrn", "confirmedName"], buildArgs: (ctx) => ({
      company_name:   ctx.confirmedName,
      company_number: ctx.confirmedCrn,
      director:       ctx.chosenDirector || undefined,
  })},
  { stage: "reputation",       needs: ["confirmedName"],        buildArgs: (ctx) => ({ company_name: ctx.confirmedName }) },
];

export default function UnderwritingWorkspace({ mondayItemId, onClose }) {
  // ── Deal context ───────────────────────────────────────────────────────
  const [deal, setDeal] = useState(null);
  const [loadingDeal, setLoadingDeal] = useState(true);
  const [layoutMode, setLayoutMode]   = useState("underwrite"); // 'underwrite' | 'split' | 'deal'

  // ── Copilot session ────────────────────────────────────────────────────
  const [session, setSession] = useState({ tool_history: [], chat_history: [] });
  const [loadingSession, setLoadingSession] = useState(true);

  // ── Orchestrator state ─────────────────────────────────────────────────
  const [hasStarted,        setHasStarted]      = useState(false);
  const [confirmedCrn,      setConfirmedCrn]    = useState(null);
  const [confirmedName,     setConfirmedName]   = useState(null);
  const [searchMatches,     setSearchMatches]   = useState([]);
  const [pendingCrnPick,    setPendingCrnPick]  = useState("");
  const [activeOfficers,    setActiveOfficers]  = useState([]);
  const [chosenDirector,    setChosenDirector]  = useState(null);
  const [isHomeowner,       setIsHomeowner]     = useState(false);
  const [toolsRun,          setToolsRun]        = useState(() => new Set());
  const [runningTool,       setRunningTool]     = useState(null);
  const [failedSteps,       setFailedSteps]     = useState(() => new Set());
  const [pausedAt,          setPausedAt]        = useState(null);
  const [orchestratorBusy,  setOrchestratorBusy]= useState(false);
  const [chatBusy,          setChatBusy]        = useState(false);

  // Refs mirror orchestrator state so the synchronous continueSweep loop can
  // read fresh values immediately after runStage updates them (React state
  // setters are async, so the closure-captured value would be stale).
  const ctxRef = useRef({
    confirmedCrn:    null,
    confirmedName:   null,
    activeOfficers:  [],
    chosenDirector:  null,
    isHomeowner:     false,
    toolsRun:        new Set(),
    failedSteps:     new Set(),
  });

  // Abort flag for the running sweep (when user resets or closes mid-run)
  const abortRef = useRef(false);

  // ── Load deal + session on open ────────────────────────────────────────
  useEffect(() => {
    let active = true;

    // Deal
    setLoadingDeal(true);
    api.get(`/underwriting-hub/deal/${mondayItemId}`)
      .then((res) => { if (active) setDeal(res.data); })
      .catch((e) => {
        console.error("loadDeal", e);
        toast.error("Couldn't load deal context from Monday");
      })
      .finally(() => { if (active) setLoadingDeal(false); });

    // Session
    setLoadingSession(true);
    api.get(`/underwriting-hub/copilot/${mondayItemId}/session`)
      .then((res) => {
        if (!active) return;
        const s = res.data || { tool_history: [], chat_history: [] };
        setSession(s);
        rebuildStateFromSession(s);
      })
      .catch(() => { /* 404 is fine — fresh deal */ })
      .finally(() => { if (active) setLoadingSession(false); });

    return () => { active = false; abortRef.current = true; };
  }, [mondayItemId]);

  // Rebuild orchestrator state when session loads (so reopening the workspace
  // shows the funnel ticks + confirmed-company state + correct pause point).
  const rebuildStateFromSession = (s) => {
    const ran  = new Set();
    const fail = new Set();
    let confCrn = null, confName = null, officers = [], lastSearchMatches = [];
    let chosenDir = null;
    for (const t of (s.tool_history || [])) {
      ran.add(t.stage);
      // company_search: 0 results is NOT a hard fail in our model — leave it as retry-able
      if (t.result && t.result.error && t.stage !== "company_search") fail.add(t.stage);
      if (t.stage === "company_search" && t.result) {
        lastSearchMatches = t.result.matches || t.result.items || [];
      }
      if (t.stage === "company_validate" && t.result) {
        // Session-storage shape is flatter than live API response — try both
        const tr      = t.result.tool_result || {};
        const profile = tr.profile || t.result.profile || t.result;
        // session: t.result.officers = {active_count, officers: [...]}
        // live:    t.result.tool_result.officers = same shape
        const offSrc = tr.officers || t.result.officers || t.result.officers_block || {};
        const offArr = Array.isArray(offSrc) ? offSrc : (offSrc.officers || []);
        confCrn  = profile.company_number || t.result.crn || null;
        confName = profile.company_name   || t.result.company_name || t.result.name || null;
        officers = offArr;
      }
      if (t.stage === "eligibility" && t.result) {
        // eligibility result records the chosen director — re-use it
        if (t.result.applying_director && !chosenDir) {
          const ad = t.result.applying_director;
          chosenDir = typeof ad === "string" ? ad : (ad.name || ad.full_name);
        }
      }
      if (t.stage === "eligibility" && t.args) {
        if (t.args.director && !chosenDir) chosenDir = t.args.director;
      }
    }
    setToolsRun(ran);
    setFailedSteps(fail);
    // Mirror into ctxRef so the orchestrator can read fresh values from ref
    ctxRef.current.toolsRun       = ran;
    ctxRef.current.failedSteps    = fail;
    ctxRef.current.confirmedCrn   = confCrn || null;
    ctxRef.current.confirmedName  = confName || null;
    ctxRef.current.activeOfficers = officers || [];
    ctxRef.current.chosenDirector = chosenDir || null;
    if (confCrn)        setConfirmedCrn(confCrn);
    if (confName)       setConfirmedName(confName);
    if (officers.length) setActiveOfficers(officers);
    if (chosenDir)      setChosenDirector(chosenDir);
    if (lastSearchMatches.length) setSearchMatches(lastSearchMatches);
    if (ran.size) setHasStarted(true);

    // Decide where the bot is paused — so the user has a visible next action
    if (ran.size === 0) {
      setPausedAt(null);
    } else if (ran.has("company_search") && !ran.has("company_validate") && !confCrn) {
      setPausedAt("awaiting_confirm");
    } else if (ran.has("company_validate") && !ran.has("eligibility") && officers.length > 1 && !chosenDir) {
      setPausedAt("awaiting_director");
    } else if (fail.size > 0) {
      setPausedAt("awaiting_user");
    } else {
      setPausedAt(null);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────
  const refreshSession = async () => {
    try {
      const res = await api.get(`/underwriting-hub/copilot/${mondayItemId}/session`);
      setSession(res.data || { tool_history: [], chat_history: [] });
    } catch (e) {
      console.warn("refreshSession", e);
    }
  };

  const markRan      = (stage) => {
    ctxRef.current.toolsRun = new Set(ctxRef.current.toolsRun).add(stage);
    setToolsRun((s) => new Set(s).add(stage));
  };
  const markFailed   = (stage) => {
    ctxRef.current.failedSteps = new Set(ctxRef.current.failedSteps).add(stage);
    setFailedSteps((s) => new Set(s).add(stage));
  };
  const clearFailed  = (stage) => {
    const n = new Set(ctxRef.current.failedSteps); n.delete(stage);
    ctxRef.current.failedSteps = n;
    setFailedSteps((s) => { const n2 = new Set(s); n2.delete(stage); return n2; });
  };

  // Setters that also keep ctxRef in sync (so continueSweep sees fresh values
  // immediately after each runStage call).
  const setConfirmedCrnSync = (v) => {
    ctxRef.current.confirmedCrn = v;
    setConfirmedCrn(v);
  };
  const setConfirmedNameSync = (v) => {
    ctxRef.current.confirmedName = v;
    setConfirmedName(v);
  };
  const setActiveOfficersSync = (v) => {
    ctxRef.current.activeOfficers = v;
    setActiveOfficers(v);
  };
  const setChosenDirectorSync = (v) => {
    ctxRef.current.chosenDirector = v;
    setChosenDirector(v);
  };
  const setIsHomeownerSync = (v) => {
    ctxRef.current.isHomeowner = v;
    setIsHomeowner(v);
  };

  // Run one stage. Returns { ok, result } so the auto-loop can decide whether
  // to continue or stop.
  //
  // SPECIAL CASE: `company_search` bypasses sf-uw-service entirely. The
  // narration adds 30-90s of Claude latency, which routinely hits Cloudflare's
  // 100s gateway timeout (502s). Direct CH lookup returns in <2s and is all
  // the picker UI actually needs. Downstream stages still go via sf-uw-service.
  const runStage = async (stage, args = {}) => {
    clearFailed(stage);
    setRunningTool(stage);

    // ── Direct CH lookup for company_search ────────────────────────────
    if (stage === "company_search") {
      try {
        const q   = (args.q || "").trim();
        const res = await api.get("/companies-house/search", { params: { q, limit: 15 } });
        const items = res.data?.items || [];
        // Normalise to the shape sf-uw-service uses (company_name + company_number)
        const matches = items.map((it) => ({
          company_number:   it.company_number,
          company_name:     it.title || it.company_name,
          company_status:   it.company_status,
          date_of_creation: it.date_of_creation,
          address_snippet:  it.address_snippet,
        }));
        setSearchMatches(matches);
        markRan(stage);
        setPausedAt("awaiting_confirm");
        return { ok: true, result: { matches } };
      } catch (e) {
        console.error("companies-house/search", e);
        markFailed(stage);
        const detail = errorDetailMessage(e, e.message || "Unknown error");
        toast.error(`Companies House search: ${detail}`);
        return { ok: false, result: { error: detail } };
      } finally {
        setRunningTool(null);
      }
    }

    // ── Local Creditsafe lookup for credit_company ────────────────────
    // sf-uw-service's Creditsafe creds are flaky (frequent 401s). Our backend
    // has working creds + a tool_creditsafe service, so route this stage
    // through our own endpoint. Result + narration are persisted locally and
    // merged into the session feed on next refresh.
    if (stage === "credit_company") {
      try {
        const res = await api.post(
          `/underwriting-hub/copilot/${mondayItemId}/credit-company-local`,
          {
            company_number: args.company_number || ctxRef.current.confirmedCrn,
            company_name:   args.company_name   || ctxRef.current.confirmedName || "",
          },
        );
        const data = res.data || {};
        markRan(stage);
        if (!data.ok) {
          markFailed(stage);
          toast.error(`credit_company: ${data.tool_result?.error || "lookup failed"}`);
        }
        await refreshSession();
        return { ok: !!data.ok, result: data };
      } catch (e) {
        console.error("credit_company local", e);
        markFailed(stage);
        const detail = errorDetailMessage(e, e.message || "Unknown error");
        toast.error(`credit_company: ${detail}`);
        return { ok: false, result: { error: detail } };
      } finally {
        setRunningTool(null);
      }
    }

    // ── Default path: async job pattern through sf-uw-service ───────────
    // We POST run-async to get a job_id (returns in <100ms), then poll until
    // the bg task completes. This avoids Cloudflare's 100s gateway timeout
    // on Claude-narration stages that can take 60-180s.
    //
    // The pollAsyncJob helper returns the sf-uw-service's response shape:
    //   { stage, tool_result: {...}, narration }
    try {
      const data = await pollAsyncJob(
        `/underwriting-hub/copilot/${mondayItemId}/run-async/${stage}`,
        { args },
      );
      markRan(stage);
      // Side-effects from specific stages
      if (stage === "company_validate") {
        // Live response shape: {stage, tool_result?: {profile, officers, ...}, narration}
        // But sf-uw-service often returns flatter:
        // {stage, profile, officers: {active_count, officers}, narration, pscs, ...}
        const tr            = data.tool_result || {};
        const profile       = tr.profile || data.profile || {};
        const offSrc        = tr.officers || data.officers || {};
        const officers      = Array.isArray(offSrc) ? offSrc : (offSrc.officers || []);
        if (officers.length) setActiveOfficersSync(officers);
        // Default-pick the sole director so eligibility doesn't gratuitously pause
        if (officers.length === 1 && !ctxRef.current.chosenDirector) {
          setChosenDirectorSync(officers[0].name || officers[0].full_name);
        }
        const cName = profile.company_name;
        const cNum  = profile.company_number;
        if (cName && !ctxRef.current.confirmedName) setConfirmedNameSync(cName);
        if (cNum  && !ctxRef.current.confirmedCrn)  setConfirmedCrnSync(cNum);
      }
      // Detect explicit fail / hard-decline reported by the service.
      // company_search is special-cased above (zero matches ≠ hard fail).
      const tr_for_fail = data.tool_result || {};
      const hardFail =
        stage !== "company_search" && (
          data.hard_fail === true ||
          data.verdict   === "fail" ||
          data.status    === "fail" ||
          tr_for_fail.hard_fail === true ||
          tr_for_fail.verdict   === "fail" ||
          tr_for_fail.status    === "fail"
        );
      if (hardFail) markFailed(stage);
      await refreshSession();
      return { ok: !hardFail, result: data };
    } catch (e) {
      console.error(`runStage ${stage}`, e);
      markFailed(stage);
      const detail = e?.message || "Unknown error";
      toast.error(`${stage}: ${detail}`);
      await refreshSession();
      return { ok: false, result: { error: detail } };
    } finally {
      setRunningTool(null);
    }
  };

  // ── Orchestrator: kicks off / continues the sweep ──────────────────────
  const continueSweep = async (startIdx = 0) => {
    setOrchestratorBusy(true);
    abortRef.current = false;
    try {
      for (let i = startIdx; i < AUTO_FLOW.length; i++) {
        if (abortRef.current) break;
        const step = AUTO_FLOW[i];
        // Use the fresh ref-mirrored state — React's setX is async so closure
        // values are stale inside this loop after a runStage call.
        const ref = ctxRef.current;

        // Skip if already run and not a fail (idempotent re-entry on continue)
        if (ref.toolsRun.has(step.stage) && !ref.failedSteps.has(step.stage)) continue;

        // Pause BEFORE running if this stage needs user input
        if (step.pauseBefore) {
          if (step.pauseBefore === "awaiting_director") {
            // Need a chosen director — pause only if we have multiple AND none picked
            if (!ref.chosenDirector && ref.activeOfficers.length > 1) {
              setPausedAt("awaiting_director");
              return;
            }
          }
        }

        // Bail if prerequisites missing (e.g. eligibility w/o confirmedCrn)
        const ctx = {
          deal,
          confirmedCrn:   ref.confirmedCrn,
          confirmedName:  ref.confirmedName,
          chosenDirector: ref.chosenDirector,
          isHomeowner:    ref.isHomeowner,
        };
        const missing = (step.needs || []).find((k) => !ctx[k]);
        if (missing) {
          // Should not happen if flow gates are coherent; log + stop softly
          console.warn(`Sweep stopped before ${step.stage}: missing ${missing}`);
          return;
        }

        const args = step.buildArgs ? step.buildArgs(ctx) : {};
        const { ok } = await runStage(step.stage, args);
        if (!ok) {
          // Hard-fail or error — stop the sweep, surface to chat
          setPausedAt("awaiting_user");
          return;
        }

        // Pause AFTER if needed
        if (step.pauseAfter) {
          setPausedAt(step.pauseAfter);
          return;
        }

        // Implicit pause for awaiting_director after company_validate if officers > 1
        // (read from ref — runStage just updated it synchronously)
        const refAfter = ctxRef.current;
        if (step.stage === "company_validate" && refAfter.activeOfficers.length > 1 && !refAfter.chosenDirector) {
          setPausedAt("awaiting_director");
          return;
        }
      }
      // Sweep completed all auto steps — leave for user to drive manual stages
      setPausedAt(null);
    } finally {
      setOrchestratorBusy(false);
    }
  };

  // Re-derive sweep continuation index after a manual gate is satisfied
  const findNextIndex = () => {
    // Continue from the first AUTO_FLOW stage not yet run
    for (let i = 0; i < AUTO_FLOW.length; i++) {
      if (!ctxRef.current.toolsRun.has(AUTO_FLOW[i].stage)) return i;
    }
    return AUTO_FLOW.length;
  };

  // ── Callbacks from FunnelSidebar ───────────────────────────────────────
  const onStart = async () => {
    setHasStarted(true);
    setPausedAt(null);
    await continueSweep(0);
  };

  const onConfirmCompany = async () => {
    const match = searchMatches.find((m) => m.company_number === pendingCrnPick);
    if (!match) return;
    setConfirmedCrnSync(match.company_number);
    setConfirmedNameSync(match.company_name || match.title);
    setPausedAt(null);
    // Continue sweep from company_validate
    const idx = AUTO_FLOW.findIndex((s) => s.stage === "company_validate");
    await continueSweep(idx);
  };

  const onContinueAfterDirector = async () => {
    setPausedAt(null);
    const idx = AUTO_FLOW.findIndex((s) => s.stage === "eligibility");
    await continueSweep(idx);
  };

  const onManualRunStage = async (stage, args = {}) => {
    // Manual single-stage re-run from the sidebar (e.g. retry, or "company_search again")
    if (stage === "company_search") {
      // Re-run search; reset confirm gate. Accept custom q from inline search input.
      setSearchMatches([]);
      setPendingCrnPick("");
      clearFailed("company_search");
      setPausedAt(null);
      const q = (args && args.q) ? args.q : (deal?.name || "");
      await runStage(stage, { q });
      // runStage already calls setPausedAt("awaiting_confirm")
      return;
    }
    // For other manual triggers (the funnel exposes them via the same callback)
    await runStage(stage, args);
  };

  // ── Affordability (BS analysis) callbacks ──────────────────────────────
  // Routed through OUR local pdf.co + 0codekit + GPT-4o pipeline because
  // sf-uw-service's /bs-analysis returns 500s on Monday-sourced PDFs.
  const onBsAnalyseFromMonday = async (assetIds) => {
    if (!assetIds || !assetIds.length) {
      toast.error("Pick at least one PDF to analyse");
      return;
    }
    setRunningTool("bs_analysis");
    try {
      const data = await pollAsyncJob(
        `/underwriting-hub/copilot/${mondayItemId}/bs-analysis-local-async`,
        {
          monday_asset_ids: assetIds,
          loan_amount:      deal?.loan_amount || null,
        },
        240000, // 4 min cap — OCR+GPT analysis can take a while
      );
      markRan("bs_analysis");
      if (!data.ok) markFailed("bs_analysis");
      await refreshSession();
      toast.success("Bank statements analysed");
    } catch (e) {
      console.error("bs_analysis", e);
      markFailed("bs_analysis");
      toast.error(`Bank statements: ${e?.message || e}`);
    } finally {
      setRunningTool(null);
    }
  };

  const onBsAnalyseUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setRunningTool("bs_analysis");
    try {
      const uploads = await Promise.all(
        fileList.map(async (f) => ({
          filename: f.name,
          data:     await fileToBase64(f),
        })),
      );
      const data = await pollAsyncJob(
        `/underwriting-hub/copilot/${mondayItemId}/bs-analysis-local-async`,
        {
          uploads,
          loan_amount: deal?.loan_amount || 0,
        },
        240000,
      );
      markRan("bs_analysis");
      if (!data.ok) markFailed("bs_analysis");
      await refreshSession();
      toast.success(`Analysed ${fileList.length} PDF(s)`);
    } catch (e) {
      console.error("bs_analysis upload", e);
      markFailed("bs_analysis");
      toast.error(`Bank statements: ${e?.message || e}`);
    } finally {
      setRunningTool(null);
    }
  };

  const onCalcOffer = async (offerArgs) => {
    setRunningTool("offer");
    try {
      await pollAsyncJob(
        `/underwriting-hub/copilot/${mondayItemId}/offer-async`,
        offerArgs,
        120000,
      );
      markRan("offer");
      await refreshSession();
      toast.success("Offer calculated");
    } catch (e) {
      console.error("offer", e);
      markFailed("offer");
      toast.error(`Offer: ${e?.message || e}`);
    } finally {
      setRunningTool(null);
    }
  };

  // ── Chat ───────────────────────────────────────────────────────────────
  const onSendMessage = async (message) => {
    setChatBusy(true);
    // Optimistically append user message
    setSession((s) => ({
      ...s,
      chat_history: [
        ...(s.chat_history || []),
        { role: "user", content: message, timestamp: new Date().toISOString() },
      ],
    }));
    try {
      await pollAsyncJob(
        `/underwriting-hub/copilot/${mondayItemId}/chat-async`,
        { message },
        120000,
      );
      await refreshSession();
    } catch (e) {
      console.error("chat", e);
      toast.error(`Chat: ${e?.message || e}`);
      await refreshSession();
    } finally {
      setChatBusy(false);
    }
  };

  // ── Reset session ──────────────────────────────────────────────────────
  const onReset = async () => {
    if (!window.confirm("Wipe this deal's copilot session?")) return;
    abortRef.current = true;
    try {
      await api.delete(`/underwriting-hub/copilot/${mondayItemId}/session`);
    } catch (e) {
      console.warn("reset", e);
    }
    setSession({ tool_history: [], chat_history: [] });
    setToolsRun(new Set());
    setFailedSteps(new Set());
    setHasStarted(false);
    setConfirmedCrn(null);
    setConfirmedName(null);
    setSearchMatches([]);
    setPendingCrnPick("");
    setActiveOfficers([]);
    setChosenDirector(null);
    setIsHomeowner(false);
    setPausedAt(null);
    // Reset the orchestrator ref too
    ctxRef.current = {
      confirmedCrn:    null,
      confirmedName:   null,
      activeOfficers:  [],
      chosenDirector:  null,
      isHomeowner:     false,
      toolsRun:        new Set(),
      failedSteps:     new Set(),
    };
    toast.success("Copilot session reset");
  };

  // ── Affordability CSV export ───────────────────────────────────────────
  const onDownloadBsCsv = async () => {
    try {
      const res = await api.get(`/underwriting-hub/copilot/${mondayItemId}/bs-analysis-csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `affordability_${deal?.sf_number || mondayItemId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV export failed — run the bank statement analysis first");
    }
  };

  // ── UW Assessment Note ─────────────────────────────────────────────────
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteBusy, setNoteBusy]             = useState(false);
  const [noteResult, setNoteResult]         = useState(null);
  const [notePosting, setNotePosting]       = useState(false);

  const onGenerateNote = async (force = false) => {
    setNoteDialogOpen(true);
    if (noteBusy || (noteResult && !force)) return;
    setNoteBusy(true);
    setNoteResult(null);
    try {
      const data = await pollAsyncJob(`/underwriting-hub/copilot/${mondayItemId}/assessment-note-async`, {}, 240000);
      setNoteResult(data);
    } catch (e) {
      toast.error(`Note generation failed: ${e?.message || e}`);
      setNoteDialogOpen(false);
    } finally {
      setNoteBusy(false);
    }
  };

  const onCopyNote = () => {
    navigator.clipboard.writeText(noteResult?.note || "");
    toast.success("Note copied to clipboard");
  };

  const onPostNote = async () => {
    if (notePosting) return;
    setNotePosting(true);
    try {
      await api.post(`/underwriting-hub/copilot/${mondayItemId}/assessment-note/post-to-monday`, { note: noteResult?.note || "" });
      toast.success("Note posted to Monday as an update");
    } catch (e) {
      toast.error(`Post failed: ${errorDetailMessage(e, e?.message)}`);
    } finally {
      setNotePosting(false);
    }
  };

  // ── Layout config ──────────────────────────────────────────────────────
  const showDeal    = layoutMode === "split" || layoutMode === "deal";
  const showFunnel  = layoutMode !== "deal";
  const showChat    = layoutMode !== "deal";

  const gridClass =
    layoutMode === "deal"
      ? "grid-cols-1"
      : layoutMode === "split"
      ? "grid-cols-1 lg:grid-cols-[1fr,1fr,1.2fr]"
      : "grid-cols-1 lg:grid-cols-[400px,1fr]";

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="!max-w-[97vw] !w-[97vw] h-[94vh] flex flex-col p-0"
        data-testid="uw-workspace"
      >
        {/* Header */}
        <DialogHeader className="px-5 pt-4 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-5 h-5 text-blue-600" />
            <span className="truncate max-w-[28rem]">
              {deal?.name || (loadingDeal ? "Loading…" : "Deal")}
            </span>
            {deal?.sf_number && (
              <Badge variant="outline" className="ml-1 font-mono text-[10px]">{deal.sf_number}</Badge>
            )}
            {deal?.status && <Badge variant="secondary" className="text-[10px]">{deal.status}</Badge>}

            {/* Layout toggle — default 'underwrite' */}
            <div className="ml-auto flex items-center gap-1 border rounded-lg p-1 bg-slate-50" data-testid="uw-layout-toggle">
              <ToggleBtn
                active={layoutMode === "underwrite"}
                onClick={() => setLayoutMode("underwrite")}
                icon={<Bot className="w-3.5 h-3.5" />}
                label="Underwrite"
                testid="uw-layout-underwrite"
              />
              <ToggleBtn
                active={layoutMode === "split"}
                onClick={() => setLayoutMode("split")}
                icon={<Layout className="w-3.5 h-3.5" />}
                label="Split"
                testid="uw-layout-split"
              />
              <ToggleBtn
                active={layoutMode === "deal"}
                onClick={() => setLayoutMode("deal")}
                icon={<Maximize2 className="w-3.5 h-3.5" />}
                label="Deal"
                testid="uw-layout-deal"
              />
            </div>

            <Button
              onClick={() => onGenerateNote(false)}
              variant="outline"
              size="sm"
              className="text-xs h-7"
              disabled={!toolsRun.size}
              title="Compose the full internal UW assessment note from every check that has run"
              data-testid="uw-generate-note-btn"
            >
              <FileText className="w-3 h-3 mr-1" /> UW Note
            </Button>

            <Button
              onClick={onReset}
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              disabled={orchestratorBusy}
              data-testid="uw-workspace-reset"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Reset
            </Button>

            {deal?.monday_url && (
              <a
                href={deal.monday_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                data-testid="uw-workspace-monday-link"
              >
                Monday <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        {(loadingDeal || loadingSession) ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading deal context…
          </div>
        ) : !deal ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <AlertCircle className="w-5 h-5 mr-2" /> Deal not found
          </div>
        ) : (
          <div className={`flex-1 overflow-hidden grid ${gridClass}`}>
            {/* DEAL CONTEXT (split / deal modes only) */}
            {showDeal && (
              <DealContextPane deal={deal} />
            )}

            {/* FUNNEL SIDEBAR */}
            {showFunnel && (
              <div
                className="overflow-hidden p-4 border-r bg-slate-50/40 flex flex-col"
                data-testid="uw-workspace-funnel"
              >
                <FunnelSidebar
                  orchestratorBusy={orchestratorBusy}
                  hasStarted={hasStarted}
                  confirmedCrn={confirmedCrn}
                  confirmedName={confirmedName}
                  searchMatches={searchMatches}
                  pendingCrnPick={pendingCrnPick}
                  setPendingCrnPick={setPendingCrnPick}
                  activeOfficers={activeOfficers}
                  chosenDirector={chosenDirector}
                  setChosenDirector={setChosenDirectorSync}
                  isHomeowner={isHomeowner}
                  setIsHomeowner={setIsHomeownerSync}
                  toolsRun={toolsRun}
                  runningTool={runningTool}
                  failedSteps={failedSteps}
                  pausedAt={pausedAt}
                  files={deal.files || []}
                  dealName={stripSfPrefix(deal.name || "")}
                  onStart={onStart}
                  onContinue={() => continueSweep(findNextIndex())}
                  onConfirmCompany={onConfirmCompany}
                  onRunStage={(stage, args) => {
                    // From the funnel inline UIs — the director-continue callback uses this
                    if (stage === "eligibility") {
                      onContinueAfterDirector();
                    } else {
                      onManualRunStage(stage, args || {});
                    }
                  }}
                  onBsAnalyseFromMonday={onBsAnalyseFromMonday}
                  onBsAnalyseUpload={onBsAnalyseUpload}
                  onCalcOffer={onCalcOffer}
                  onDownloadBsCsv={onDownloadBsCsv}
                />
              </div>
            )}

            {/* CHAT PANEL */}
            {showChat && (
              <div className="overflow-hidden p-4 bg-white flex flex-col" data-testid="uw-workspace-chat">
                <ChatPanel
                  session={session}
                  chatBusy={chatBusy}
                  orchestratorBusy={orchestratorBusy}
                  onSendMessage={onSendMessage}
                  emptyPlaceholder={
                    hasStarted ? null : (
                      <>
                        Click <strong>Start analysis</strong> on the left to begin.
                        <p className="text-xs mt-3">
                          I&apos;ll run through eligibility, affordability, credit, network &amp; KYC,
                          pausing only for the decisions only you can make.
                        </p>
                      </>
                    )
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* UW Assessment Note dialog */}
        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="!max-w-3xl h-[80vh] flex flex-col" data-testid="uw-note-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <FileText className="w-4 h-4 text-blue-600" />
                UW Assessment Note {deal?.sf_number ? `— ${deal.sf_number}` : ""}
                {noteResult?.cumulative_risk_score != null && (
                  <Badge
                    variant="outline"
                    className={`ml-2 text-[10px] ${noteResult.cumulative_risk_score <= 1.5 ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                    data-testid="uw-note-score-badge"
                  >
                    Risk {noteResult.cumulative_risk_score}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            {noteBusy ? (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2">
                <Loader2 className="w-6 h-6 animate-spin" />
                <div className="text-sm">Composing the note from every check that has run…</div>
                <div className="text-xs">(30–90 seconds)</div>
              </div>
            ) : noteResult ? (
              <>
                <pre
                  className="flex-1 overflow-auto text-xs whitespace-pre-wrap bg-slate-50 border rounded-lg p-4 font-mono leading-relaxed"
                  data-testid="uw-note-text"
                >
                  {noteResult.note}
                </pre>
                {(noteResult.unassessed || []).length > 0 && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Not assessed (run those checks for a complete score): {noteResult.unassessed.join(", ")}
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => onGenerateNote(true)} data-testid="uw-note-regenerate">
                    <RotateCcw className="w-3 h-3 mr-1" /> Regenerate
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={onCopyNote} data-testid="uw-note-copy">
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                  <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700" onClick={onPostNote} disabled={notePosting} data-testid="uw-note-post-monday">
                    {notePosting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                    Post to Monday
                  </Button>
                </div>
              </>
            ) : null}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}


// ─── Sub-components ─────────────────────────────────────────────────────

function DealContextPane({ deal }) {
  return (
    <div className="overflow-auto p-4 border-r bg-slate-50/40 space-y-4" data-testid="uw-workspace-deal">
      <Card>
        <CardContent className="p-4 grid grid-cols-2 gap-3 text-sm">
          <Field label="Loan amount" value={deal.loan_amount ? `£${deal.loan_amount.toLocaleString()}` : "—"} />
          <Field label="Term"        value={deal.duration || "—"} />
          <Field label="Factor"      value={deal.factor || "—"} />
          <Field label="Frequency"   value={deal.columns?.text_mkr4c5sw || "—"} />
          <Field label="Payment amt" value={deal.payment_amount || "—"} />
          <Field label="Acct manager" value={deal.account_manager || "—"} />
          <Field label="Broker"       value={deal.broker || "—"} />
          <Field label="Group"        value={deal.group_name || "—"} />
          <Field label="Contact"      value={deal.contact_name || "—"} />
          <Field label="Contact email" value={deal.contact_email || "—"} mono />
          <Field label="Customer email" value={deal.customer_email || "—"} mono />
          <Field
            label="Created"
            value={deal.date_created || (deal.created_at ? new Date(deal.created_at).toLocaleDateString("en-GB") : "—")}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1">
            <Paperclip className="w-4 h-4 text-muted-foreground" />
            Files attached ({(deal.files || []).length})
          </h3>
          {(deal.files || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No files attached to this Monday item.</p>
          ) : (
            <div className="space-y-2" data-testid="uw-deal-files">
              {deal.files.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-2 p-2 border rounded hover:bg-slate-50 text-xs">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileIcon ext={f.extension} />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{f.name}</div>
                      <div className="text-muted-foreground">
                        {fmtSize(f.size)}
                        {f.uploaded_by && ` · ${f.uploaded_by}`}
                        {f.source === "update" && " · from note"}
                      </div>
                    </div>
                  </div>
                  <a href={f.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <Button variant="ghost" size="sm">
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
            Recent notes from Monday ({deal.updates?.length || 0})
          </h3>
          {(deal.updates?.length || 0) === 0 ? (
            <p className="text-xs text-muted-foreground">No updates on this item yet.</p>
          ) : (
            <div className="space-y-3 max-h-[40vh] overflow-auto">
              {deal.updates.slice(0, 20).map((u) => (
                <div key={u.id} className="text-xs border-l-2 border-slate-200 pl-3">
                  <div className="flex justify-between text-muted-foreground mb-0.5">
                    <span className="font-medium">{u.creator?.name || "Unknown"}</span>
                    <span>{u.created_at ? new Date(u.created_at).toLocaleString("en-GB") : ""}</span>
                  </div>
                  <div className="whitespace-pre-wrap text-slate-700">
                    {(u.text_body || "").slice(0, 600)}
                    {u.text_body && u.text_body.length > 600 && <span className="text-muted-foreground">…</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function ToggleBtn({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition ${
        active ? "bg-white shadow-sm text-blue-700" : "text-slate-600 hover:text-slate-900"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}


function Field({ label, value, mono = false }) {
  return (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-medium ${mono ? "font-mono text-xs break-all" : ""}`}>{value}</div>
    </div>
  );
}


function FileIcon({ ext }) {
  const e = String(ext || "").toLowerCase().replace(".", "");
  const colour = ["pdf"].includes(e)
    ? "bg-red-100 text-red-700"
    : ["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(e)
    ? "bg-violet-100 text-violet-700"
    : ["xls", "xlsx", "csv"].includes(e)
    ? "bg-green-100 text-green-700"
    : ["doc", "docx"].includes(e)
    ? "bg-blue-100 text-blue-700"
    : "bg-slate-100 text-slate-600";
  return (
    <div className={`w-7 h-7 rounded flex items-center justify-center text-[10px] font-semibold uppercase shrink-0 ${colour}`}>
      {e || "?"}
    </div>
  );
}


function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}


function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => {
      const str = String(reader.result || "");
      // Strip "data:application/pdf;base64," prefix
      const i   = str.indexOf(",");
      resolve(i >= 0 ? str.slice(i + 1) : str);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}


// "LAST, First Middle" → { first, last, full }
function splitDirectorName(name) {
  if (!name) return { first: "", last: "", full: "" };
  const [last, rest] = name.split(",").map((s) => s.trim());
  return {
    first: (rest || "").split(/\s+/)[0] || "",
    last:  last || "",
    full:  name,
  };
}

// Monday items are named "SF-26353 - ARRP TRADING LIMITED" — strip the SF
// prefix so Companies House searches use just the company name.
function stripSfPrefix(name) {
  if (!name) return "";
  return name.replace(/^\s*SF-?\d+[\s-]*[-–]?\s*/i, "").trim() || name;
}


// Kick off an async job on the backend, poll until done. Throws on failure
// or timeout. The backend job pattern survives Cloudflare's 100s gateway
// timeout because each poll is a fast call.
async function pollAsyncJob(kickoffPath, body, maxMs = 180000) {
  // 1) Kick off
  const kickRes = await api.post(kickoffPath, body);
  const jobId   = kickRes.data?.job_id;
  if (!jobId) {
    throw new Error("Backend didn't return a job_id");
  }
  // 2) Poll
  const start    = Date.now();
  const interval = 1500; // 1.5s
  while (true) {
    if (Date.now() - start > maxMs) {
      throw new Error(`Timed out after ${Math.round(maxMs / 1000)}s waiting for job`);
    }
    await new Promise((r) => setTimeout(r, interval));
    let pollRes;
    try {
      pollRes = await api.get(`/underwriting-hub/copilot/jobs/${jobId}`);
    } catch (e) {
      // 502s on the poll endpoint are rare but possible — keep trying
      console.warn("poll error", e?.response?.status, e?.message);
      continue;
    }
    const j = pollRes.data || {};
    if (j.status === "done") {
      return j.result || {};
    }
    if (j.status === "failed") {
      throw new Error(j.error || "Underwriting service error");
    }
    // pending → keep polling
  }
}
